/** How a canvas is drawn, and which half of the renderer draws each node of its scene. */

import { isControlKind } from "@nebulize/overlay/interact";
import type { SceneDocument } from "@nebulize/sdk";

/** How a canvas is drawn. Two answers, and the manifest picks one. */
export type CanvasLayout = "composited" | "flowed";

/** Absolutely positioned, one Pixi stage, every node where the scene put it. */
export const COMPOSITED = "composited";

/** Flowed DOM for everything carrying text or taking a press, so a filtered node leaves no hole. */
export const FLOWED = "flowed";

/** The attachment with no person behind it. */
const PAIRED = "paired";

/** The manifest facts this rule reads, structurally, so a caller passes its own `CanvasDecl`. */
export interface CanvasReach {
  readonly attach?: string | null;
  readonly on_machine_screen?: boolean | null;
}

/** How a canvas declaring this is drawn. Undeclared flows: a person's address reached it. */
export function canvasLayout(declared: CanvasReach | undefined): CanvasLayout {
  if (declared === undefined) return FLOWED;
  if (declared.on_machine_screen === true) return COMPOSITED;
  return declared.attach === PAIRED ? COMPOSITED : FLOWED;
}

/** Which nodes the interact half draws: only controls under a composite, everything in a flow. */
export function controlLayerOf(layout: CanvasLayout): "all" | "controls" {
  return layout === COMPOSITED ? "controls" : "all";
}

type SceneNode = NonNullable<SceneDocument["nodes"]>[number];

/** The structural kind the render half draws without drawing anything of its own. */
const GROUP_KIND = "group";

/** The nodes the render half draws in a flow: everything the interact half cannot, plus their groups. */
function pictureNodes(nodes: readonly SceneNode[]): SceneNode[] {
  const kept: SceneNode[] = [];
  for (const node of nodes) {
    if (!isControlKind(node.kind)) {
      kept.push(node);
      continue;
    }
    if (node.kind !== GROUP_KIND) continue;
    const children = pictureNodes(node.children ?? []);
    if (children.length > 0) kept.push({ ...node, children });
  }
  return kept;
}

/** The documents the render half is handed. A flowed document with no picture in it is dropped. */
export function pictureScenes(
  scenes: readonly SceneDocument[],
  layout: CanvasLayout,
): readonly SceneDocument[] {
  if (layout === COMPOSITED) return scenes;

  const kept: SceneDocument[] = [];
  for (const document of scenes) {
    const nodes = pictureNodes(document.nodes ?? []);
    if (nodes.length > 0) kept.push({ ...document, nodes });
  }
  return kept;
}

/** Whether a placement rectangle means anything on this layout. A flow has no coordinate. */
export function placeable(layout: CanvasLayout): boolean {
  return layout === COMPOSITED;
}

/** Whether any node here is a control leaf: a real button, bar or line of text, not a bare group. */
function hasControlLeaf(nodes: readonly SceneNode[]): boolean {
  for (const node of nodes) {
    if (!isControlKind(node.kind)) continue;
    if (node.kind !== GROUP_KIND) return true;
    if (hasControlLeaf(node.children ?? [])) return true;
  }
  return false;
}

/** Whether either half has anything at all to draw from these documents on this layout. */
export function documentsDraw(scenes: readonly SceneDocument[], layout: CanvasLayout): boolean {
  if (layout === COMPOSITED) return scenes.length > 0;
  if (pictureScenes(scenes, layout).length > 0) return true;
  return scenes.some((document) => hasControlLeaf(document.nodes ?? []));
}
